import { getSupabaseBrowserClient } from "@/lib/supabase/client";
import type {
  AdminLandingPage,
  PublicLandingPage,
  SaveLandingPageInput,
} from "@/lib/landing/types";

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === "object"
    ? (value as Record<string, unknown>)
    : {};
}

function stringValue(value: unknown, fallback = "") {
  return typeof value === "string" ? value : fallback;
}

export async function getPublicLandingPage(
  slug = "home",
): Promise<PublicLandingPage | null> {
  const supabase = getSupabaseBrowserClient();
  const { data, error } = await supabase.rpc("get_public_landing_page", {
    page_slug: slug,
  });

  if (error) {
    throw error;
  }
  if (!data) {
    return null;
  }

  const payload = asRecord(data);
  return {
    slug: stringValue(payload.slug, slug),
    title: stringValue(payload.title, "PraxisTárs"),
    html: stringValue(payload.html),
    css: stringValue(payload.css),
    updatedAt:
      typeof payload.updatedAt === "string" ? payload.updatedAt : null,
  };
}

export async function getAdminLandingPage(
  slug = "home",
): Promise<AdminLandingPage | null> {
  const supabase = getSupabaseBrowserClient();
  const { data, error } = await supabase.rpc("get_admin_landing_page", {
    page_slug: slug,
  });

  if (error) {
    throw error;
  }
  if (!data) {
    return null;
  }

  const payload = asRecord(data);
  return {
    id: stringValue(payload.id),
    slug: stringValue(payload.slug, slug),
    title: stringValue(payload.title, "PraxisTárs"),
    html: stringValue(payload.html),
    css: stringValue(payload.css),
    projectData: asRecord(payload.projectData),
    status: payload.status === "published" ? "published" : "draft",
    hasUnpublishedChanges: payload.hasUnpublishedChanges === true,
    publishedAt:
      typeof payload.publishedAt === "string" ? payload.publishedAt : null,
    updatedAt:
      typeof payload.updatedAt === "string" ? payload.updatedAt : null,
  };
}

export async function saveAdminLandingPage(
  input: SaveLandingPageInput,
): Promise<string> {
  const supabase = getSupabaseBrowserClient();
  const { data, error } = await supabase.rpc("save_admin_landing_page", {
    page_slug: input.slug,
    page_title: input.title,
    page_html: input.html,
    page_css: input.css,
    page_project_data: input.projectData,
  });

  if (error) {
    throw error;
  }

  return String(data);
}

export async function publishAdminLandingPage(input: {
  slug: string;
  publish: boolean;
}): Promise<void> {
  const supabase = getSupabaseBrowserClient();
  const { error } = await supabase.rpc("publish_admin_landing_page", {
    page_slug: input.slug,
    should_publish: input.publish,
  });

  if (error) {
    throw error;
  }
}
